import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

type ListMarker =
  | { tag: 'ul' }
  | { tag: 'ol'; type: 'a' | 'i' | null; prefix: string | null; value: number };

interface WordListItem {
  paragraph: HTMLElement;
  level: number;
  marker: string;
}

interface OpenList {
  level: number;
  list: HTMLElement;
  marker: ListMarker;
  lastItem: HTMLElement;
}

const MSO_LIST_LEVEL = /\bl\d+\s+level(\d+)/i;
const DECIMAL_MARKER = /^(\d+(?:\.\d+)*)[.)]?$/;
const ALPHA_MARKER = /^\(?([a-z])[.)]$/i;
const ROMAN_MARKER = /^\(?([ivxlcdm]+)[.)]$/i;
const ROMAN_DIGITS: Record<string, number> = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 };
const ALIGNABLE = new Set(['P', 'H1', 'H2', 'H3']);

/** Waarde van één CSS-eigenschap uit het style-attribuut (ook mso-eigenschappen, die de browser negeert). */
function styleValue(element: Element, property: string): string {
  const match = new RegExp(`(?:^|;)\\s*${property}\\s*:\\s*([^;]+)`, 'i').exec(element.getAttribute('style') ?? '');
  return match ? match[1].trim().toLowerCase() : '';
}

function unwrap(element: Element) {
  element.replaceWith(...Array.from(element.childNodes));
}

function rename(element: Element, tag: string): HTMLElement {
  const renamed = element.ownerDocument.createElement(tag);
  renamed.append(...Array.from(element.childNodes));
  element.replaceWith(renamed);
  return renamed;
}

function wrapContents(element: Element, tag: string) {
  const wrapper = element.ownerDocument.createElement(tag);
  wrapper.append(...Array.from(element.childNodes));
  element.appendChild(wrapper);
}

function romanValue(roman: string): number {
  let total = 0;
  for (let index = 0; index < roman.length; index++) {
    const value = ROMAN_DIGITS[roman[index].toLowerCase()];
    const next = ROMAN_DIGITS[roman[index + 1]?.toLowerCase()] ?? 0;
    total += value < next ? -value : value;
  }
  return total;
}

/**
 * Word zet het nummer van een lijstpunt als tekst in de alinea ("a." of "2.1" plus spaties).
 * `previous` is het vorige punt op hetzelfde niveau: "i." is daarna de negende letter,
 * maar aan het begin van een lijst de Romeinse één.
 */
function parseMarker(marker: string, previous: ListMarker | null): ListMarker {
  const decimal = DECIMAL_MARKER.exec(marker);
  if (decimal) {
    const parts = decimal[1].split('.');
    const value = Number(parts.pop());
    return { tag: 'ol', type: null, prefix: parts.length ? `${parts.join('.')}.` : null, value };
  }
  const alpha = ALPHA_MARKER.exec(marker);
  const roman = ROMAN_MARKER.exec(marker);
  const previousType = previous?.tag === 'ol' ? previous.type : null;
  const romanFirst = previousType === 'i' || (previousType !== 'a' && alpha?.[1].toLowerCase() === 'i');
  if (alpha && !(roman && romanFirst)) {
    return { tag: 'ol', type: 'a', prefix: null, value: alpha[1].toLowerCase().charCodeAt(0) - 96 };
  }
  if (roman) return { tag: 'ol', type: 'i', prefix: null, value: romanValue(roman[1]) };
  return { tag: 'ul' };
}

function continuesList(previous: ListMarker, marker: ListMarker): boolean {
  if (previous.tag === 'ul' || marker.tag === 'ul') return previous.tag === marker.tag;
  return previous.type === marker.type && previous.prefix === marker.prefix && marker.value === previous.value + 1;
}

function createList(doc: Document, marker: ListMarker): HTMLElement {
  const list = doc.createElement(marker.tag);
  if (marker.tag === 'ol') {
    if (marker.type) list.setAttribute('type', marker.type);
    if (marker.value !== 1) list.setAttribute('start', String(marker.value));
    if (marker.prefix) list.setAttribute('data-number-prefix', marker.prefix);
  }
  return list;
}

/** Het nummer staat in een span met `mso-list:Ignore`; die valt weg uit de tekst. */
function takeWordMarker(element: Element): string {
  const span = Array.from(element.querySelectorAll('span')).find(
    (candidate) => styleValue(candidate, 'mso-list') === 'ignore'
  );
  if (!span) return '';
  span.remove();
  return (span.textContent ?? '').replace(/\s+/g, ' ').trim();
}

function readWordListItem(paragraph: HTMLElement): WordListItem | null {
  const level = MSO_LIST_LEVEL.exec(styleValue(paragraph, 'mso-list'));
  if (!level) return null;
  return { paragraph, level: Number(level[1]), marker: takeWordMarker(paragraph) };
}

function replaceWordListRun(items: WordListItem[]) {
  const doc = items[0].paragraph.ownerDocument;
  const fragment = doc.createDocumentFragment();
  const open: OpenList[] = [];

  for (const item of items) {
    while (open.length && open[open.length - 1].level > item.level) open.pop();
    let current: OpenList | undefined = open[open.length - 1];
    const sameLevel = current?.level === item.level ? current : undefined;
    const marker = parseMarker(item.marker, sameLevel?.marker ?? null);

    if (!sameLevel || !continuesList(sameLevel.marker, marker)) {
      if (sameLevel) open.pop();
      const list = createList(doc, marker);
      (open[open.length - 1]?.lastItem ?? fragment).appendChild(list);
      current = { level: item.level, list, marker, lastItem: list };
      open.push(current);
    }

    const listItem = doc.createElement('li');
    const paragraph = doc.createElement('p');
    paragraph.append(...Array.from(item.paragraph.childNodes));
    listItem.appendChild(paragraph);
    current!.list.appendChild(listItem);
    current!.lastItem = listItem;
    current!.marker = marker;
  }

  items[0].paragraph.before(fragment);
  for (const item of items) item.paragraph.remove();
}

/** Opeenvolgende Word-lijstalinea's worden één (geneste) lijst. */
function convertWordLists(root: HTMLElement) {
  let run: WordListItem[] = [];
  for (const paragraph of Array.from(root.querySelectorAll('p'))) {
    const item = readWordListItem(paragraph);
    if (!item) continue;
    if (run.length && run[run.length - 1].paragraph.nextElementSibling !== paragraph) {
      replaceWordListRun(run);
      run = [];
    }
    run.push(item);
  }
  if (run.length) replaceWordListRun(run);

  // Genummerde koppen ("Artikel 1") houden hun nummer, met één spatie in plaats van een tab.
  for (const heading of Array.from(root.querySelectorAll('h1, h2, h3, h4, h5, h6'))) {
    const marker = takeWordMarker(heading);
    if (marker) heading.prepend(`${marker} `);
  }
  for (const tab of Array.from(root.querySelectorAll('span'))) {
    if (styleValue(tab, 'mso-tab-count')) tab.replaceWith(' ');
  }
}

/** Google Docs en websites zetten a/b/c- en i/ii-nummering in CSS in plaats van `type`. */
function readListTypes(root: HTMLElement) {
  for (const list of Array.from(root.querySelectorAll('ol'))) {
    const type = list.getAttribute('type');
    const styleType = styleValue(list, 'list-style-type') || styleValue(list.querySelector('li') ?? list, 'list-style-type');
    if (type === 'A' || type === 'a' || /alpha|latin/.test(styleType)) list.setAttribute('type', 'a');
    else if (type === 'I' || type === 'i' || /roman/.test(styleType)) list.setAttribute('type', 'i');
    else list.removeAttribute('type');
  }
}

function isBold(element: Element): boolean {
  const weight = styleValue(element, 'font-weight');
  return weight === 'bold' || weight === 'bolder' || Number(weight) >= 600;
}

/** Vet, cursief en onderstreept uit CSS (Google Docs) worden echte opmaak; de rest van de CSS valt weg. */
function inlineStylesToMarks(root: HTMLElement) {
  // Google Docs verpakt alles in <b style="font-weight:normal">.
  for (const bold of Array.from(root.querySelectorAll('b, strong'))) {
    const weight = styleValue(bold, 'font-weight');
    if (weight === 'normal' || (weight !== '' && Number(weight) < 600)) unwrap(bold);
  }
  for (const element of Array.from(root.querySelectorAll('span[style], font[style], a[style], p[style]'))) {
    if (element.closest('h1, h2, h3, h4, h5, h6')) continue;
    if (isBold(element) && !element.closest('b, strong')) wrapContents(element, 'strong');
    if (/italic|oblique/.test(styleValue(element, 'font-style')) && !element.closest('i, em')) {
      wrapContents(element, 'em');
    }
    const decoration = `${styleValue(element, 'text-decoration')} ${styleValue(element, 'text-decoration-line')}`;
    if (decoration.includes('underline') && !element.closest('u')) wrapContents(element, 'u');
  }
}

/** Lettergrootte, font, kleur, uitvullen, marges en klassen weg; centreren en rechts uitlijnen blijven. */
function stripPresentation(root: HTMLElement) {
  for (const element of Array.from(root.querySelectorAll('*'))) {
    if (element.tagName.includes(':') || element.tagName === 'FONT') {
      unwrap(element);
      continue;
    }
    // Word plakt afbeeldingen als lokaal bestand; die laden nergens.
    if (element.tagName === 'IMG' && /^file:/i.test(element.getAttribute('src') ?? '')) {
      element.remove();
      continue;
    }
    const target = /^H[4-6]$/.test(element.tagName) ? rename(element, 'h3') : element;
    const align = styleValue(element, 'text-align');
    target.removeAttribute('style');
    target.removeAttribute('class');
    target.removeAttribute('lang');
    if (ALIGNABLE.has(target.tagName) && (align === 'center' || align === 'right')) {
      target.setAttribute('style', `text-align: ${align}`);
    }
  }
}

/**
 * Een witregel in een alinea (`<br><br>`, uit een e-mail of website) wordt een
 * nieuwe alinea. Anders blijft het één lange alinea met vetgedrukte regels als
 * kopjes, die de paginering alleen bij een regeleinde over vellen kan verdelen.
 */
function splitBlankLines(root: HTMLElement) {
  for (const paragraph of Array.from(root.querySelectorAll('p'))) {
    const parts: ChildNode[][] = [[]];
    let breaks: ChildNode[] = [];
    for (const node of Array.from(paragraph.childNodes)) {
      const blank = node.nodeType === Node.TEXT_NODE && !(node.textContent ?? '').trim();
      if (node.nodeName === 'BR' || (blank && breaks.length > 0)) {
        breaks.push(node);
        continue;
      }
      const current = parts[parts.length - 1];
      if (current.length > 0 && breaks.filter((item) => item.nodeName === 'BR').length >= 2) {
        parts.push([node]);
      } else {
        current.push(...breaks, node);
      }
      breaks = [];
    }
    if (parts.length === 1) continue;
    parts[parts.length - 1].push(...breaks);
    let previous: Element = paragraph;
    for (const nodes of parts.slice(1)) {
      const next = paragraph.cloneNode(false) as HTMLElement;
      next.append(...nodes);
      previous.after(next);
      previous = next;
    }
    // Wat overblijft zijn de witregels tussen de delen.
    paragraph.replaceChildren(...parts[0]);
  }
}

/** Lege regels uit Word (`<o:p>&nbsp;</o:p>`) en Google Docs (`<br>` tussen alinea's) geven gaten. */
function removeEmptyBlocks(root: HTMLElement) {
  for (const block of Array.from(root.querySelectorAll('p, h1, h2, h3'))) {
    if (!block.querySelector('img') && !(block.textContent ?? '').replace(/\s/g, '')) block.remove();
  }
  for (const child of Array.from(root.children)) {
    if (child.tagName === 'BR') child.remove();
  }
}

/**
 * Tekst uit Word, Google Docs of een website nam eigen lettergroottes, fonts, kleuren en
 * uitvulling mee, die vóór de vaste stijlen van de offerte gingen; lijsten kwamen binnen
 * als tekst ("a.   ") en lege regels als lege alinea's. Na het plakken blijven de structuur
 * (koppen, lijsten met hun nummering, alinea's, links, tabellen) en vet, cursief en
 * onderstreept over. Kopiëren binnen de editor houdt alle opmaak: ProseMirror zet dan
 * `data-pm-slice` in de HTML.
 */
export function cleanPastedHtml(html: string): string {
  if (html.includes('data-pm-slice')) return html;
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  for (const element of Array.from(body.querySelectorAll('style, script, meta, link, title, xml'))) {
    element.remove();
  }
  convertWordLists(body);
  readListTypes(body);
  inlineStylesToMarks(body);
  stripPresentation(body);
  splitBlankLines(body);
  removeEmptyBlocks(body);
  return body.innerHTML;
}

export const PasteCleanup = Extension.create({
  name: 'pasteCleanup',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('pasteCleanup'),
        props: { transformPastedHTML: cleanPastedHtml },
      }),
    ];
  },
});
